import React, { FC } from 'react';
import { CollapsibleBase, CollapsibleBaseClassNames } from './CollapsibleBase';
import { twMerge } from '@shared/utils/twMerge';

export type SectionCollapsibleProps = {
  title: React.ReactNode;
  children: React.ReactNode;

  /** Optional node aligned to the far right in the header. */
  actions?: React.ReactNode;

  /** Visual density. */
  density?: 'normal' | 'tight';

  /** Colors (overrideable). */
  headerBg?: string; // e.g., 'bg-blueTone'
  bodyBg?: string; // e.g., 'bg-white'
  divider?: boolean; // header bottom divider

  /** Chevron side. */
  iconPosition?: 'left' | 'right';

  /** Control open state or start open. */
  open?: boolean;
  defaultOpen?: boolean;
  onToggle?: (o: boolean) => void;

  /** Extra re-measure trigger. */
  resize?: number | string;

  /** Extra class name hooks to merge. */
  classNames?: Partial<CollapsibleBaseClassNames>;
};

export const SectionCollapsible: FC<SectionCollapsibleProps> = ({
  title,
  actions,
  children,
  density = 'normal',
  headerBg = 'bg-blueTone',
  bodyBg = 'bg-white',
  divider = false,
  iconPosition = 'left',
  open,
  defaultOpen,
  onToggle,
  resize,
  classNames = {},
}) => {
  const dense = density === 'tight';

  return (
    <CollapsibleBase
      iconPosition={iconPosition}
      open={open}
      defaultOpen={defaultOpen}
      onToggle={onToggle}
      resize={resize}
      classNames={{
        outer: twMerge('rounded-md overflow-hidden', classNames.outer),

        // Keep vertical spacing OUTSIDE, if desired
        container: twMerge(dense ? 'mt-0' : 'mt-[2.4rem]', classNames.container),

        header: twMerge(
          // height + padding presets
          dense ? 'h-10 pl-6 pr-2' : 'h-[4.8rem]',
          headerBg,
          divider ? 'border-b border-waterLight' : 'border-b-0',
          classNames.header
        ),

        // animated shell — keep clean
        content: twMerge(classNames.content),

        // inner body wrapper — padding and bg
        body: twMerge(dense ? 'p-2' : 'p-3', 'rounded-b-md', bodyBg, classNames.body),

        // chevron
        iconBtn: twMerge(classNames.iconBtn),
        icon: twMerge(classNames.icon),
      }}
      label={
        <div className="flex w-full items-center gap-2">
          <div className="min-w-0">{title}</div>
          {actions ? (
            <div
              className="ml-auto"
              onClick={event => event.stopPropagation()}
            >
              {actions}
            </div>
          ) : null}
        </div>
      }
    >
      {children}
    </CollapsibleBase>
  );
};
